export default function NormanTravelTimingSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Travel &amp; Passport Timing for Norman Residents
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Understanding how Norman travelers actually move helps us anticipate the passport urgencies that arise here. From OU study abroad cycles to OU Health medical rotations, Cleveland County travel operates on a unique academic and healthcare rhythm.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          {/* OU Study Abroad */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-graduation-cap-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">OU Study Abroad &amp; Jerusalem Center</h3>
                <p className="text-gray-400 text-xs">University of Oklahoma campus — Norman, OK</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              The University of Oklahoma sends over 2,800 students abroad annually. The OU Jerusalem Center alone sends hundreds of students per semester. Application deadlines typically fall in October and February, but many students do not think about their passport until after acceptance. First-time applicants (many college students) require an in-person acceptance clerk visit. We guide OU students through the exact Norman location, forms, and timeline so they do not miss their program orientation.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Apply by June for fall semester abroad and by November for spring. The August and December rushes are our busiest — start early to avoid the peak.
              </p>
            </div>
          </div>

          {/* OU Health Medical Travel */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-hospital-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">OU Health Medical Conferences &amp; Rotations</h3>
                <p className="text-gray-400 text-xs">OU Health Medical Center — Norman, OK</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              OU Health is one of Oklahoma's largest medical systems. International clinical rotations, WHO conferences, and global health programs require valid passports with specific validity windows. Medical professionals with last-minute scheduling changes need rapid passport turnaround. We regularly process urgent passport renewals for OU Health physicians whose conference schedules shift with less than two weeks notice.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Many countries require 6 months of passport validity beyond your travel date. For medical professionals, that means checking your passport before any conference registration — not the week before.
              </p>
            </div>
          </div>

          {/* Campus Corner & Alumni */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-building-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Campus Corner &amp; Young Professional Travel</h3>
                <p className="text-gray-400 text-xs">Downtown Norman — Campus Corner district</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Campus Corner and downtown Norman attract young professionals and recent alumni who travel frequently for work and leisure. Many have allowed passports to lapse since their study abroad days and need quick renewals for business trips, destination weddings, and international vacations. The walkable downtown and proximity to OKC's Will Rogers World Airport make Norman an ideal base for spontaneous international travel.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Recent OU alumni often have passports that expired during COVID when travel paused. Check your expiration now — many are surprised to find theirs expired.
              </p>
            </div>
          </div>

          {/* Family & Holiday Travel */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-home-heart-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Norman Family &amp; Holiday Travel</h3>
                <p className="text-gray-400 text-xs">Norman, Noble, Moore &amp; surrounding communities</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Norman families travel internationally during Thanksgiving, Christmas, and spring break. Many discover expired passports when they go to book flights — sometimes just weeks before departure. Child passports add complexity: both parents must appear together or submit a notarized consent form. We guide Norman families through child passport requirements and coordinate group processing for extended families traveling together.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Norman families: check every child's passport as soon as you start trip planning. Child passports are only valid for 5 years, and many families are caught off guard by expired child passports.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-calendar-event-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Peak Passport Rush Seasons in Norman</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              August (fall semester abroad), December (spring semester abroad), and March (spring break) are the three busiest months for Norman passport processing. Starting your application at least 8 weeks before these peaks ensures you avoid the rush and get the best processing rates.
            </p>
          </div>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0"
          >
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}